import { createLogger } from '@/core/helpers/logger.helper';

const logger = createLogger('SpeedProvider');

export interface LatLng {
  lat: number;
  lng: number;
}

export interface ThinkPhaseResult {
  avgSpeedMps: number;
  sampleCount: number;
  totalDistanceM: number;
  durationMs: number;
}

export interface SpeedProvider {
  readonly currentSpeedMps: number;
  readonly lastCoords: LatLng | null;
  readonly lastTimestamp: number;
  readonly sessionDistanceM: number;
  /** False while the device gives us nothing we can measure movement with. */
  readonly hasSignal: boolean;

  start(): void;
  stop(): void;
  reset(): void;
  getDistanceBetween(a: LatLng, b: LatLng): number;
  startThinkPhase(): void;
  endThinkPhase(): ThinkPhaseResult;
  consumeSessionDistance(): number;
}

const EARTH_RADIUS = 6_371_000;

export function haversineDistanceCoords(a: LatLng, b: LatLng): number {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

const SAMPLE_COUNT = 5;
const DECAY_START_MS = 2000;
const DECAY_DURATION_MS = 1500;
const MAX_AGE = 0;

const ACCURACY_GATE_M = 20;
const DOPPLER_TRUST_THRESHOLD_MPS = 0.3;
const DIFF_TRUST_THRESHOLD_MPS = 0.5;
const MAX_PLAUSIBLE_SPEED_MPS = 8;
const STOPPED_CONFIRM_COUNT = 2;

/** How long we go without a usable fix before calling the signal lost. */
const SIGNAL_TIMEOUT_MS = 10_000;
const COARSE_LOG_INTERVAL_MS = 10_000;

const GEOLOCATION_ERROR_NAMES: Record<number, string> = {
  1: 'PERMISSION_DENIED',
  2: 'POSITION_UNAVAILABLE',
  3: 'TIMEOUT',
};

export class WebSpeedProvider implements SpeedProvider {
  private _speedMps = 0;
  private _lastCoords: LatLng | null = null;
  private _lastTimestamp = 0;
  private watchId: number | null = null;
  private samples: number[] = [];
  private decayTimer: ReturnType<typeof setInterval> | null = null;
  private rawSpeed = 0;
  private thinkPositions: { coords: LatLng; timestamp: number }[] = [];
  private isThinkPhaseActive = false;
  private stoppedConfirmCount = 0;
  private isMoving = false;
  private _sessionDistanceM = 0;
  private _lastSyncDistanceM = 0;
  private startedAt = 0;
  private lastUsableFixAt = 0;
  private lastErrorCode: number | null = null;
  private lastCoarseLogAt = 0;

  get currentSpeedMps(): number {
    return this._speedMps;
  }

  /**
   * A device without GNSS (a Wi-Fi only tablet, say) keeps reporting positions
   * far too coarse to clear the accuracy gate, so the speed sits at zero and
   * looks exactly like standing still. This tells the two apart.
   */
  get hasSignal(): boolean {
    if (this.lastErrorCode != null) return false;

    // Before the first usable fix we allow the same grace period from the start,
    // otherwise every session opens by claiming the signal is gone.
    const since = this.lastUsableFixAt || this.startedAt;
    if (!since) return false;

    return Date.now() - since <= SIGNAL_TIMEOUT_MS;
  }

  get lastCoords(): LatLng | null {
    return this._lastCoords;
  }

  get lastTimestamp(): number {
    return this._lastTimestamp;
  }

  get sessionDistanceM(): number {
    return this._sessionDistanceM;
  }

  consumeSessionDistance(): number {
    const delta = this._sessionDistanceM - this._lastSyncDistanceM;
    this._lastSyncDistanceM = this._sessionDistanceM;
    return delta;
  }

  startThinkPhase(): void {
    this.isThinkPhaseActive = true;
    this.thinkPositions = [];
    if (this._lastCoords && this._lastTimestamp) {
      this.thinkPositions.push({ coords: this._lastCoords, timestamp: this._lastTimestamp });
    }
  }

  endThinkPhase(): ThinkPhaseResult {
    this.isThinkPhaseActive = false;
    const positions = this.thinkPositions;
    this.thinkPositions = [];

    if (positions.length < 2) {
      return { avgSpeedMps: 0, sampleCount: positions.length, totalDistanceM: 0, durationMs: 0 };
    }

    let totalDistance = 0;
    for (let i = 1; i < positions.length; i++) {
      totalDistance += haversineDistanceCoords(positions[i - 1].coords, positions[i].coords);
    }

    const durationMs = positions[positions.length - 1].timestamp - positions[0].timestamp;
    const avgSpeedMps = durationMs > 0 ? totalDistance / (durationMs / 1000) : 0;

    return {
      avgSpeedMps,
      sampleCount: positions.length,
      totalDistanceM: totalDistance,
      durationMs,
    };
  }

  start(): void {
    this.stop();
    this._speedMps = 0;
    this._lastCoords = null;
    this._lastTimestamp = 0;
    this.samples = [];
    this.rawSpeed = 0;
    this.stoppedConfirmCount = 0;
    this.isMoving = false;
    this._sessionDistanceM = 0;
    this._lastSyncDistanceM = 0;
    this.startedAt = Date.now();
    this.lastUsableFixAt = 0;
    this.lastErrorCode = null;
    this.lastCoarseLogAt = 0;

    if (!navigator.geolocation) {
      this.lastErrorCode = 2;
      logger.warn('Geolocation is unavailable in this browser.');
      return;
    }

    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this.onPosition(pos),
      (error) => this.onError(error),
      {
        enableHighAccuracy: true,
        maximumAge: MAX_AGE,
      },
    );

    this.decayTimer = setInterval(() => this.applyDecay(), 200);
  }

  stop(): void {
    if (this.watchId != null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.decayTimer != null) {
      clearInterval(this.decayTimer);
      this.decayTimer = null;
    }
    this._speedMps = 0;
    this.rawSpeed = 0;
    this.samples = [];
    this._sessionDistanceM = 0;
    this._lastSyncDistanceM = 0;
    this.startedAt = 0;
    this.lastUsableFixAt = 0;
    this.lastErrorCode = null;
  }

  reset(): void {
    this._lastCoords = null;
    this._lastTimestamp = 0;
    this.samples = [];
    this._speedMps = 0;
    this.rawSpeed = 0;
    this.stoppedConfirmCount = 0;
    this.isMoving = false;
  }

  getDistanceBetween(a: LatLng, b: LatLng): number {
    return haversineDistanceCoords(a, b);
  }

  private onError(error: GeolocationPositionError): void {
    this.lastErrorCode = error.code;
    const name = GEOLOCATION_ERROR_NAMES[error.code] ?? `code ${error.code}`;
    logger.warn(`Geolocation watch failed with ${name}: ${error.message}`);
  }

  private onPosition(pos: GeolocationPosition): void {
    // A fix arriving at all clears the error, however unusable it turns out to be.
    this.lastErrorCode = null;

    if (pos.coords.accuracy > ACCURACY_GATE_M) {
      this.logCoarseFix(pos.coords.accuracy);
      return;
    }

    this.lastUsableFixAt = Date.now();

    const coords: LatLng = { lat: pos.coords.latitude, lng: pos.coords.longitude };
    const now = pos.timestamp;
    const dopplerSpeed = pos.coords.speed;

    let diffSpeed: number | null = null;
    let segmentDistance = 0;
    if (this._lastCoords && this._lastTimestamp) {
      segmentDistance = haversineDistanceCoords(this._lastCoords, coords);
      const dtSec = (now - this._lastTimestamp) / 1000;
      if (dtSec > 0) {
        diffSpeed = segmentDistance > pos.coords.accuracy * 0.5 ? segmentDistance / dtSec : 0;
      }
    }

    let speed: number;

    if (dopplerSpeed != null && dopplerSpeed > DOPPLER_TRUST_THRESHOLD_MPS) {
      speed = dopplerSpeed;
      this.isMoving = true;
      this.stoppedConfirmCount = 0;
    } else if (diffSpeed != null && diffSpeed > DIFF_TRUST_THRESHOLD_MPS) {
      speed = diffSpeed;
      this.isMoving = true;
      this.stoppedConfirmCount = 0;
    } else {
      this.stoppedConfirmCount++;
      if (this.stoppedConfirmCount >= STOPPED_CONFIRM_COUNT) {
        speed = 0;
        this.isMoving = false;
      } else {
        speed = this.rawSpeed * 0.5;
      }
    }

    if (this.isMoving && segmentDistance > 0) {
      this._sessionDistanceM += segmentDistance;
    }

    this.addSample(speed);

    this._lastCoords = coords;
    this._lastTimestamp = now;

    if (this.isThinkPhaseActive) {
      this.thinkPositions.push({ coords, timestamp: now });
    }
  }

  // Fixes this coarse arrive several times a second, so one line every so often
  // is enough to show a tester the device is positioning off Wi-Fi, not GPS.
  private logCoarseFix(accuracyM: number): void {
    const now = Date.now();
    if (now - this.lastCoarseLogAt < COARSE_LOG_INTERVAL_MS) return;
    this.lastCoarseLogAt = now;
    logger.warn(`Dropping fixes accurate to ${Math.round(accuracyM)}m, the gate is ${ACCURACY_GATE_M}m.`);
  }

  private addSample(speed: number): void {
    if (speed > MAX_PLAUSIBLE_SPEED_MPS) return;

    this.samples.push(speed);
    if (this.samples.length > SAMPLE_COUNT) {
      this.samples.shift();
    }

    const sorted = [...this.samples].sort((a, b) => a - b);
    this.rawSpeed = sorted[Math.floor(sorted.length / 2)];
    this._speedMps = this.rawSpeed;
  }

  private applyDecay(): void {
    if (!this._lastTimestamp) return;
    const elapsed = Date.now() - this._lastTimestamp;
    if (elapsed <= DECAY_START_MS) {
      this._speedMps = this.rawSpeed;
      return;
    }
    const fadeMs = elapsed - DECAY_START_MS;
    const factor = Math.max(0, 1 - fadeMs / DECAY_DURATION_MS);
    this._speedMps = this.rawSpeed * factor;
  }
}
